import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
    useBeforeUnload,
    useLocation,
    useNavigate,
} from "react-router-dom";
import { useLanguage } from "../../hooks/useLanguage";

import { links } from "../../constants/links";
import { rawCountries } from "../../constants/phoneCode";
import { LIST_LANGUAGE_ENUM } from "../../constants";
import { getCountriesAndBlockedPhonePrefix, sendPhoneNumber } from "../../services/otp/otp.service";

import ReactCountryFlag from "react-country-flag";
import { Box, styled, Typography } from "@mui/material";
import { KeyboardArrowDown as KeyboardArrowDownIcon } from "@mui/icons-material";

import LogoHeader from "../../components/LogoHeader";
import { Loading } from "../../components";
import SelectCountry from "../authenticate/login/SelectCountry";
import { Footer } from "../../components/Footer";

const defaultCountryCode = process?.env?.REACT_APP_DEFAULT_COUNTRY?.toLowerCase() ?? "";
const defaultCountryPhonePrefix: string = String(rawCountries.find(array => array[2] === defaultCountryCode)?.[3] ?? "");

const PhoneInputWrapper = styled(Box)({
    marginTop: 40,
    border: "1px solid #E6E8EC",
    borderRadius: 8,
    padding: 10,
    paddingLeft: 16,
    display: "flex",
    alignItems: "center",
    "& .react-tel-input": {
      width: "100%"
    },
    "& .form-control": {
      border: "none",
      borderLeft: "1px solid #E6E8EC",
      borderRadius: "unset",
      paddingLeft: 10,
      marginLeft: 10,
      fontWeight: 600,
      color: "#777E90",
      width: "inherit"
    },
    "& .flag-dropdown": {
      display: "none",
      backgroundColor: "unset",
      border: "none"
    },
    "& .emojiFlag": {
      borderRadius: 2
    },
});


const OTPInsertPhone = () => {
    const navigate = useNavigate();

    if (localStorage.getItem("otp-page-expired") === "1" || localStorage.getItem("otp-phone-verified") === "1") {
        navigate(links.OTP_PAGE_EXPIRED);
    }

    const location = useLocation();

    const { t } = useTranslation();

    const inputRef = useRef<HTMLInputElement | null>(null);

    const [actualPhoneValue, setActualPhoneValue] = useState<any>("");

    const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);

    const [inProgress, setInProgress] = useState(false);

    const [showSelectCountry, setShowSelectCountry] = useState(false);

    const [availableCountries, setAvailableCountries] = useState([]);

    const [blockedPrefixesByCountryCode, setBlockedPrefixesByCountryCode] = useState(null);

    const [countryMeta, setCountryMeta] = useState({ phonePrefix: defaultCountryPhonePrefix, code: defaultCountryCode });

    const { phonePrefix: countryPhonePrefix, code: countryCode } = countryMeta;

    const blockPrefixes = blockedPrefixesByCountryCode !== null
        && Object.prototype.hasOwnProperty.call(blockedPrefixesByCountryCode, countryCode)
        && Array.isArray(blockedPrefixesByCountryCode[countryCode])
            ? blockedPrefixesByCountryCode[countryCode]
            : [];

    const { engageSession } = location.state || {};

    const { language } = useLanguage();

    useEffect(
        () => {
            getCountriesAndBlockedPhonePrefix()
                .then(
                    res => {
                        if (res.status === 200) {
                            const { data } = res;

                            if (Array.isArray(data.countries)) {
                                setAvailableCountries(data.countries.slice());
                            }

                            if (data.blocked_prefixes_by_country_code) {
                                setBlockedPrefixesByCountryCode(data.blocked_prefixes_by_country_code);
                            }
                        }
                    }
                );

            history.pushState(null, "", null);

            const callback = (event: any) => {
                history.pushState(null, "", null);
            };
            window.addEventListener("popstate", callback);

            return () => {
                window.removeEventListener("popstate", callback);
            };
        },
        []
    );

    useBeforeUnload(
        () => {
            localStorage.setItem("otp-page-expired", "1");
        }
    );

    const handleSelectCountry = (value: any) => {
        setShowSelectCountry(false);
        setCountryMeta({ phonePrefix: value[3], code: value[2] });

        setTimeout(
            () => {
                if (inputRef.current !== null) {
                    inputRef.current.focus();
                }
            },
            0
        );
    };

    const handleOnChange = (event: any) => {
        const value = event.target.value;
        let phoneNumber = typeof countryPhonePrefix === "string" && value.startsWith(countryPhonePrefix)
            ? value.replace(countryPhonePrefix, "")
            : value;

        if (blockPrefixes.some(s => phoneNumber.startsWith(s))) {
            setIsPhoneNumberValid(false);
        } else {
            setIsPhoneNumberValid(true);
        }

        setActualPhoneValue(phoneNumber);
    };

    const handleOnSubmit = () => {
        setInProgress(true);

        const sanitizedPhoneValue = actualPhoneValue.replace(/^0+/, "");

        setActualPhoneValue(sanitizedPhoneValue);

        sendPhoneNumber(countryCode, countryPhonePrefix, sanitizedPhoneValue)
            .then(
                res => {
                    setInProgress(false);

                    if (res.status === 200) {
                        localStorage.setItem("otp-phone-verified", "1");
                        navigate(links.OTP_INSERT_OTP, {
                            state: {
                                countryCode,
                                countryPhonePrefix,
                                phoneNumber: sanitizedPhoneValue,
                                OTPExpiredTimeInSeconds: res.data?.otp_expired_time,
                                engageSession: engageSession,
                                ...location.state
                            }
                        });
                    }
                }
            )
            .catch(
                error => {
                    const errorCode = error.response.data?.error_code;

                    if (errorCode === "maximum_attempts_reached") {
                        navigate(links.OTP_MAX_ATTEMPT_REACHED);
                    } else if (errorCode === "line_user_verified") {
                        navigate(links.OTP_PHONE_NUMBER_ALREADY_REGISTERED);
                    } else if (errorCode === "invalid_phone_number") {
                        setIsPhoneNumberValid(false);
                        setActualPhoneValue("");
                        setInProgress(false);
                    } else {
                        navigate(links.OTP_PAGE_EXPIRED);
                    }
                }
            );
    };

    const isSubmitButtonDisabled = !isPhoneNumberValid || actualPhoneValue === "";
    const containerHeight = language === LIST_LANGUAGE_ENUM.JA ? "auto" : "46%";

    return inProgress
        ? <Loading />
        : showSelectCountry
            ? <SelectCountry setShowSelectCountry={setShowSelectCountry} setCountry={handleSelectCountry} availableCountries={availableCountries} />
            : (
                <div style={{ height: "100%", maxHeight: "100%", padding: "14px 20px", position: "relative" }}>
                    <div>
                    <LogoHeader enableLanguage={true}/>
                    </div>
                    <div style={{ height: containerHeight, display: "flex", flexDirection: "column", rowGap: 16, justifyContent: "center", alignItems: "center", marginRight: "auto", marginLeft: "auto", textAlign: "center" }}>
                        <Typography
                            variant="body1"
                            style={{
                                fontWeight: 900,
                                fontSize: 22,
                                color: "#5c5c5c" /* #5888C4 */
                            }}
                        >
                            {t("verifyYourAccount")}
                        </Typography>
                        {
                            language !== LIST_LANGUAGE_ENUM.JA &&
                            <Typography variant="body1" style={{ color: "#5c5c5c", fontSize: 16 }}>{t("pleaseEnterYourMobilePhoneNumber")}</Typography>
                        }
                        {language === LIST_LANGUAGE_ENUM.JA && (
                            <div style={{ whiteSpace: "pre-line", color: "#5c5c5c", fontSize: 16 }}>
                                <Typography variant="body1" fontSize={16}>
                                    {t("pleaseEnterYourMobilePhoneNumber")}
                                </Typography>
                                <Typography variant="body1" fontSize={16}>
                                    {t("pleaseEnterYourMobilePhoneNumberJA1")}
                                    <a href="https://adcoinz.lmig.co.jp/news/smssupport" style={{ textDecoration: "underline", color: "blue" }} target="_blank" rel="noopener noreferrer">{t("pleaseEnterYourMobilePhoneNumberJA2")}</a>
                                    {t("pleaseEnterYourMobilePhoneNumberJA3")}
                                </Typography>
                            </div>
                        )
                        }
                        {
                            language !== LIST_LANGUAGE_ENUM.JA &&
                            blockPrefixes.length > 0
                                ? <Typography variant="body1" style={{ color: "#5c5c5c", fontSize: 16 }}>{t("certainNumbersStartingWithXXXCannotBeUsed", { phonePrefix: blockPrefixes.join(", ") })}</Typography>
                                : null
                        }
                        {
                            isPhoneNumberValid
                                ? null
                                : <Typography variant="body1" style={{ color: "red", fontSize: 16 }}>{t("invalidPhoneNumber")}</Typography>
                        }
                    </div>
                    <PhoneInputWrapper>
                        <Box display="flex" alignItems="center" onClick={() => setShowSelectCountry(true)}>
                            <ReactCountryFlag
                                countryCode={countryCode}
                                svg
                                className="emojiFlag"
                                style={{
                                    width: "1.5rem",
                                    height: "1em",
                                }}
                            />
                            <Typography variant="subtitle2" style={{ fontWeight: 700, marginLeft: 4 }}>+{countryPhonePrefix}</Typography>
                            <KeyboardArrowDownIcon fontSize="small" sx={{ color: "#777E90", margin: "0 5px" }} />
                        </Box>
                        <input
                            ref={inputRef}
                            inputMode="numeric"
                            type="number"
                            required
                            name="phone"
                            value={actualPhoneValue}
                            onChange={handleOnChange}
                            style={{ border: "none", outline: 0, fontSize: 14, height: 35 }}
                        />
                    </PhoneInputWrapper>
                    <button
                        disabled={isSubmitButtonDisabled}
                        onClick={handleOnSubmit}
                        tabIndex={0}
                        className="otp-btn"
                        style={
                            {
                                borderRadius: 16,
                                marginTop: 24,
                                padding: 15,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%"
                            }
                        }
                    >
                        <Typography
                            variant="body1"
                            style={{ fontWeight: "bold" }}
                            fontSize={15}
                        >
                            {t("receiveCodeViaSMS")}
                            {
                                t("receiveCodeViaSMS2") !== "receiveCodeViaSMS2"
                                    ? (
                                        <>
                                            <br />
                                            {t("receiveCodeViaSMS2")}
                                        </>
                                    )
                                    : null
                            }
                        </Typography>
                    </button>
                    <Footer />
                </div>
            );
};

export default OTPInsertPhone;
